import { Button } from "@/components/ui/button";
import ProposalApprovalConfirmDialog from "@/components/proposal/ProposalApprovalConfirmDialog";
import {
  buildSalespersonContactHref,
  formatApprovalDateDisplay,
} from "@/lib/proposalCustomerActions";
import {
  proposalApprovalSuccessPath,
  proposalApprovalSuccessPathForCredential,
  saveProposalApprovalSuccess,
} from "@/lib/proposalApprovalSuccess";
import { trpc } from "@/lib/trpc";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import {
  DEFAULT_APPROVAL_WHATSAPP_COUNTRY,
  resolveApprovalWhatsAppCountry,
  seedApprovalWhatsAppFromContact,
} from "@shared/proposalApprovalOtp";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";

const SALES_TEXT = "#1A2744";
const SALES_ACCENT = "#C8A96A";

type ProposalCustomerApprovalActionsProps = {
  proposalId: number;
  proposalRef: string;
  status: string;
  approvedAt?: string | Date | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerContact?: string | null;
  customerPricing?: ProposalCustomerPricingDisplay | null;
  commercialSummary?: {
    originalPrice: number;
    finalApprovedPrice: number;
    showFinalAgreedPrice: boolean;
  } | null;
  approvalDisabled?: boolean;
  salesperson?: {
    name?: string | null;
    email?: string | null;
    contact?: string | null;
  } | null;
  onApproved?: () => void | Promise<void>;
  /**
   * Secure Link B credential — required for public (anonymous) approval / OTP / evidence.
   * Absent for authenticated contexts (Clarity One, staff preview) which use their own
   * proposalId-scoped procedures.
   */
  linkBCredential?: { readableRef: string; sequence: number; token: string } | null;
};

export default function ProposalCustomerApprovalActions({
  proposalId,
  proposalRef,
  status,
  approvedAt,
  customerName,
  customerEmail,
  customerContact,
  customerPricing,
  commercialSummary,
  approvalDisabled = false,
  salesperson,
  onApproved,
  linkBCredential,
}: ProposalCustomerApprovalActionsProps) {
  const [, setLocation] = useLocation();
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [otpStep, setOtpStep] = useState<"details" | "otp">("details");
  const [approverName, setApproverName] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [whatsappCountry, setWhatsappCountry] = useState(DEFAULT_APPROVAL_WHATSAPP_COUNTRY);
  const [approvalEmail, setApprovalEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [maskedWhatsApp, setMaskedWhatsApp] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpNotice, setOtpNotice] = useState<string | null>(null);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const [approved, setApproved] = useState(false);
  const seeded = useRef(false);

  const evidenceQuery = trpc.proposals.getPublicProposalApprovalEvidence.useQuery(
    { credential: linkBCredential! },
    { enabled: Boolean(linkBCredential) && (status === "approved" || approved), refetchOnWindowFocus: false },
  );

  const resolvedPricing = useMemo<ProposalCustomerPricingDisplay>(() => {
    if (customerPricing) return customerPricing;
    const proposalTotal = commercialSummary?.originalPrice ?? 0;
    const finalAgreedPrice = commercialSummary?.finalApprovedPrice ?? proposalTotal;
    const showFinalAgreedPrice = commercialSummary?.showFinalAgreedPrice ?? false;
    const discountAmount = Math.max(0, Math.round((proposalTotal - finalAgreedPrice) * 100) / 100);
    const showDiscount = showFinalAgreedPrice && discountAmount > 0.004;
    return {
      proposalTotal,
      finalAgreedPrice,
      showFinalAgreedPrice,
      discountAmount: showDiscount ? discountAmount : 0,
      discountPercentage:
        showDiscount && proposalTotal > 0.004
          ? Math.round((discountAmount / proposalTotal) * 10000) / 100
          : 0,
      showDiscount,
      showDiscountPercentage: false,
      invoiceGstEnabled: false,
      gstAmount: 0,
      showGst: false,
      finalAmountPayable: finalAgreedPrice,
      showFinalAmountPayable: showFinalAgreedPrice,
    };
  }, [customerPricing, commercialSummary]);

  const requestOtp = trpc.proposals.requestPublicProposalApprovalOtp.useMutation({
    onMutate: () => {
      setOtpNotice(null);
      setOtpError(null);
    },
    onSuccess: (res) => {
      setMaskedWhatsApp(res.maskedWhatsApp);
      if (res.rateLimited) {
        setOtpStep("otp");
        setResendCooldownSec(Math.max(1, res.retryAfterSeconds ?? 60));
        const message = "Too many codes have been requested. Please wait for the countdown, then try again.";
        setOtpError(message);
        toast.message(message);
        return;
      }
      const wasResend = otpStep === "otp";
      setOtpStep("otp");
      setOtpCode("");
      setResendCooldownSec(Math.max(1, res.retryAfterSeconds ?? 30));
      setOtpNotice(
        wasResend
          ? "A new WhatsApp code has been requested. Only the newest code will work."
          : "Your WhatsApp verification code is on its way.",
      );
      toast.success(
        wasResend
          ? "A new WhatsApp code is on its way."
          : "Your WhatsApp verification code is on its way.",
      );
      if ("devOtp" in res && typeof (res as { devOtp?: string }).devOtp === "string") {
        setOtpCode((res as { devOtp: string }).devOtp);
      }
    },
    onError: (e) => {
      const message = e.message || "We could not send a verification code. Please try again.";
      setOtpError(message);
      toast.error(message);
    },
  });

  const approveProposal = trpc.proposals.approvePublicProposal.useMutation({
    onSuccess: async (res) => {
      const payload = (res as { successScreen?: Parameters<typeof saveProposalApprovalSuccess>[0] })
        .successScreen;
      if (payload) {
        saveProposalApprovalSuccess(payload);
        setLocation(
          linkBCredential
            ? proposalApprovalSuccessPathForCredential(linkBCredential)
            : proposalApprovalSuccessPath(proposalId),
        );
        return;
      }
      setApproved(true);
      setApproveDialogOpen(false);
      await onApproved?.();
      toast.success("Thank you — your proposal is approved.");
    },
    onError: (e) => {
      const friendly =
        e.message || "That code could not be verified. Please try again or request a new code.";
      setOtpError(friendly);
      toast.error(friendly);
    },
  });

  useEffect(() => {
    if (seeded.current) return;
    if (customerName?.trim()) setApproverName(customerName.trim());
    if (customerContact?.trim()) {
      const seededPhone = seedApprovalWhatsAppFromContact(customerContact);
      setWhatsappCountry(seededPhone.country);
      setWhatsappNumber(seededPhone.number);
    }
    if (customerEmail?.trim()) setApprovalEmail(customerEmail.trim());
    seeded.current = true;
  }, [customerName, customerContact, customerEmail]);

  useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const t = window.setTimeout(() => setResendCooldownSec((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldownSec]);

  const contactHref = buildSalespersonContactHref({
    proposalRef,
    email: salesperson?.email,
    contact: salesperson?.contact,
  });
  const isApproved = approved || status === "approved";
  const evidence = evidenceQuery.data;
  const picName = salesperson?.name?.trim() ?? "";
  const contactButtonLabel = picName
    ? `CONTACT ${picName.toLocaleUpperCase("en-SG")}`
    : "CONTACT SALESPERSON";
  const approveButtonDisabled = isApproved || approvalDisabled;

  return (
    <section className="min-w-0 max-w-full space-y-5">
      <p className="min-w-0 break-words text-xl font-semibold sm:text-2xl">Ready to bring this home?</p>
      {!isApproved ? (
        <p className="max-w-md text-sm leading-relaxed text-[#1A2744]/65">
          Approving is quick and secure. We will ask for your name and WhatsApp number, then send a
          one-time code so your approval is verified before it is recorded.
        </p>
      ) : null}
      <div className="flex w-full min-w-0 flex-col gap-3 md:flex-row md:flex-wrap lg:flex-col">
        <Button
          className="h-auto min-h-9 w-full min-w-0 max-w-full whitespace-normal py-2 text-center bg-[#1A2744] text-white hover:bg-[#1A2744]/90 disabled:opacity-60 md:flex-1 md:basis-0 lg:flex-none [flex-shrink:1]"
          disabled={approveButtonDisabled}
          onClick={() => {
            if (approvalDisabled) return;
            setOtpStep("details");
            setOtpCode("");
            setOtpError(null);
            setOtpNotice(null);
            setApproveDialogOpen(true);
          }}
        >
          Approve this proposal
        </Button>
        <Button
          variant="outline"
          className="h-auto min-h-9 w-full min-w-0 max-w-full whitespace-normal break-words border-[#1A2744]/20 py-2.5 text-center leading-tight md:flex-1 md:basis-0 lg:flex-none px-3 [flex-shrink:1]"
          disabled={!contactHref}
          onClick={() => {
            if (!contactHref) return;
            window.open(contactHref, "_blank", "noopener,noreferrer");
          }}
        >
          {contactButtonLabel}
        </Button>
      </div>

      <ProposalApprovalConfirmDialog
        open={approveDialogOpen && !approvalDisabled}
        onOpenChange={(open) => {
          setApproveDialogOpen(open);
          if (!open) {
            setOtpStep("details");
            setOtpCode("");
            setOtpError(null);
            setOtpNotice(null);
          }
        }}
        proposalRef={proposalRef}
        customerPricing={resolvedPricing}
        approvalEmail={approvalEmail}
        onApprovalEmailChange={setApprovalEmail}
        confirming={approveProposal.isPending}
        confirmDisabled={isApproved}
        onConfirm={() => undefined}
        otpStep={otpStep}
        approverName={approverName}
        onApproverNameChange={setApproverName}
        whatsappNumber={whatsappNumber}
        onWhatsappNumberChange={setWhatsappNumber}
        whatsappCountry={whatsappCountry}
        onWhatsappCountryChange={(value) => setWhatsappCountry(resolveApprovalWhatsAppCountry(value))}
        otpCode={otpCode}
        onOtpCodeChange={setOtpCode}
        maskedWhatsApp={maskedWhatsApp}
        sendingOtp={requestOtp.isPending}
        verifyingOtp={approveProposal.isPending}
        resendCooldownSec={resendCooldownSec}
        otpNotice={otpNotice}
        otpError={otpError}
        onSendOtp={() => {
          if (!linkBCredential) return;
          requestOtp.mutate({
            credential: linkBCredential,
            approverName,
            whatsappNumber,
            whatsappCountry,
            approvalEmail: approvalEmail.trim() || undefined,
          });
        }}
        onVerifyOtp={() => {
          if (!linkBCredential) return;
          setOtpError(null);
          approveProposal.mutate({
            credential: linkBCredential,
            approverName,
            whatsappNumber,
            whatsappCountry,
            approvalEmail: approvalEmail.trim() || undefined,
            otpCode: otpCode.trim(),
          });
        }}
        onBackToDetails={() => {
          setOtpStep("details");
          setOtpCode("");
          setOtpError(null);
          setOtpNotice(null);
        }}
      />

      {isApproved ? (
        <div
          className="rounded-xl border border-[#C8A96A]/35 bg-[#C8A96A]/08 px-4 py-4 text-sm leading-relaxed"
          style={{ color: SALES_TEXT }}
          data-testid="proposal-approval-recorded"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: SALES_ACCENT }}>
            Approved
          </p>
          {evidence ? (
            <dl className="mt-3 space-y-2.5">
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-[#1A2744]/50">Approved by</dt>
                <dd className="mt-0.5 min-w-0 break-words font-medium">{evidence.approverName}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-[#1A2744]/50">
                  Verified WhatsApp
                </dt>
                <dd className="mt-0.5 font-medium">{evidence.verifiedWhatsAppDisplay}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-[#1A2744]/50">Approved on</dt>
                <dd className="mt-0.5 min-w-0 break-words font-medium">
                  {evidence.approvedAtSingapore || formatApprovalDateDisplay(evidence.approvedAt)}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-[#1A2744]/50">
                  Confirmation email
                </dt>
                <dd className="mt-0.5 min-w-0 break-all font-medium">
                  {evidence.confirmationEmailAddress
                    ? evidence.confirmationEmailAddress
                    : "No confirmation email was requested"}
                  {evidence.confirmationEmailAddress && evidence.confirmationEmailStatus === "queued"
                    ? " · sending…"
                    : null}
                  {evidence.confirmationEmailAddress && evidence.confirmationEmailStatus === "failed"
                    ? " · delivery issue (approval remains valid)"
                    : null}
                  {evidence.confirmationEmailAddress && evidence.confirmationEmailStatus === "sent"
                    ? " · sent"
                    : null}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="mt-2 text-[#1A2744]/80">
              This proposal was approved on{" "}
              <span className="font-medium text-[#1A2744]">
                {formatApprovalDateDisplay(approvedAt)}
              </span>
              .
            </p>
          )}
        </div>
      ) : null}
      <div className="min-w-0 space-y-1 pt-2 text-sm break-words">
        <p className="font-medium">{salesperson?.name?.trim() || "Your Clarity consultant"}</p>
        <p>{salesperson?.contact?.trim() || "—"}</p>
        <p>{salesperson?.email?.trim() || "—"}</p>
      </div>
    </section>
  );
}
